import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import OpsIdentityVerification from './OpsIdentityVerification';

const OpsOrgActions = ({ summary }) => {
  const { orgId } = useParams();
  const isClosedOrg = String(summary?.org_status || '').toLowerCase() === 'closed';
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [reopenPreview, setReopenPreview] = useState(null);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [throttleStatus, setThrottleStatus] = useState({ type: '', message: '' });
  const [selectedEmail, setSelectedEmail] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const [throttleLoading, setThrottleLoading] = useState(false);
  const [throttles, setThrottles] = useState([]);
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [resetStatus, setResetStatus] = useState({ type: '', message: '' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [overrideStatus, setOverrideStatus] = useState({ type: '', message: '' });
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overrideScope, setOverrideScope] = useState('org');
  const [overrideUserId, setOverrideUserId] = useState('');
  const [overrideType, setOverrideType] = useState('enable_otp_email');
  const [overrideMode, setOverrideMode] = useState('temporary');
  const [overrideDuration, setOverrideDuration] = useState(15);
  const [overrideUserFilter, setOverrideUserFilter] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideRemoving, setOverrideRemoving] = useState(false);
  const [overrideReverting, setOverrideReverting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showPermanentConfirm, setShowPermanentConfirm] = useState(false);
  const [passwordActionStatus, setPasswordActionStatus] = useState({ type: '', message: '' });
  const [passwordActionUserId, setPasswordActionUserId] = useState('');
  const [passwordActionFilter, setPasswordActionFilter] = useState('');
  const [passwordActionType, setPasswordActionType] = useState('force_password_reset');
  const [passwordActionSubmitting, setPasswordActionSubmitting] = useState(false);
  const [sessionStatus, setSessionStatus] = useState({ type: '', message: '' });
  const [sessionUserId, setSessionUserId] = useState('');
  const [sessionUserFilter, setSessionUserFilter] = useState('');
  const [sessionPortal, setSessionPortal] = useState('all');
  const [sessionSubmitting, setSessionSubmitting] = useState(false);
  const [snapshotUserId, setSnapshotUserId] = useState('');
  const [snapshotFilter, setSnapshotFilter] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [showAcknowledge, setShowAcknowledge] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [openActionSection, setOpenActionSection] = useState('');
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);

  useEffect(() => {
    if (!status.message) return;
    const timer = window.setTimeout(() => setStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [status.message]);

  useEffect(() => {
    if (!throttleStatus.message) return;
    const timer = window.setTimeout(() => setThrottleStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [throttleStatus.message]);

  useEffect(() => {
    if (!resetStatus.message) return;
    const timer = window.setTimeout(() => setResetStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [resetStatus.message]);

  useEffect(() => {
    if (!overrideStatus.message) return;
    const timer = window.setTimeout(() => setOverrideStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [overrideStatus.message]);
  useEffect(() => {
    if (!passwordActionStatus.message) return;
    const timer = window.setTimeout(() => setPasswordActionStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [passwordActionStatus.message]);
  useEffect(() => {
    if (!sessionStatus.message) return;
    const timer = window.setTimeout(() => setSessionStatus({ type: '', message: '' }), 4000);
    return () => window.clearTimeout(timer);
  }, [sessionStatus.message]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = isClosedOrg
          ? await api.get(`/acct-mgmt/v1/orgs/${orgId}/reopen/preview`)
          : await api.get(`/acct-mgmt/v1/orgs/${orgId}/users`);
        if (!mounted) return;
        const fetchedUsers = isClosedOrg
          ? (response.data?.eligible_account_managers || []).map((user) => ({
            id: user.user_id,
            full_name: user.full_name,
            email: user.email,
            roles: user.roles,
          }))
          : (response.data?.users || []);
        setUsers(fetchedUsers);
        if (isClosedOrg) {
          setReopenPreview(response.data || null);
        }
        if (!selectedEmail && fetchedUsers.length > 0) {
          setSelectedEmail(fetchedUsers[0].email || '');
        }
      } catch (err) {
        if (!mounted) return;
        setStatus({ type: 'danger', message: err.response?.data?.error || (isClosedOrg ? 'Failed to load reopen candidates.' : 'Failed to load users.') });
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };
    if (orgId) loadUsers();
    return () => {
      mounted = false;
    };
  }, [orgId, isClosedOrg]);

  useEffect(() => {
    setShowTools(false);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const stored = window.localStorage.getItem(`finbase.ops.showTools.${orgId}`);
    setShowTools(stored === 'true');
  }, [orgId]);

  const loadOverrides = async () => {
    setOverridesLoading(true);
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/auth-overrides`);
      setOverrides(response.data?.overrides || []);
    } catch (err) {
      setOverrideStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load auth overrides.' });
    } finally {
      setOverridesLoading(false);
    }
  };

  const selectedUser = useMemo(
    () => users.find((user) => String(user.id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const filteredSnapshotUsers = useMemo(() => {
    const term = snapshotFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, snapshotFilter]);

  const filteredByEmail = useMemo(() => {
    const term = emailFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, emailFilter]);

  const filteredUsers = useMemo(() => {
    const term = userFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, userFilter]);

  const filteredPasswordUsers = useMemo(() => {
    const term = passwordActionFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, passwordActionFilter]);

  const filteredSessionUsers = useMemo(() => {
    const term = sessionUserFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, sessionUserFilter]);

  const loadThrottles = async (email) => {
    if (!email) {
      setThrottles([]);
      return;
    }
    setThrottleLoading(true);
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/auth-throttles`, {
        params: { email },
      });
      setThrottles(response.data?.throttles || []);
    } catch (err) {
      setThrottleStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load auth throttles.' });
    } finally {
      setThrottleLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedEmail) {
      setThrottles([]);
      setSelectedPurposes([]);
      return;
    }
    loadThrottles(selectedEmail);
  }, [selectedEmail]);

  const togglePurpose = (purpose) => {
    setSelectedPurposes((prev) => (
      prev.includes(purpose)
        ? prev.filter((item) => item !== purpose)
        : [...prev, purpose]
    ));
  };

  const formatPurpose = (value) => (value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  useEffect(() => {
    if (!snapshotUserId) {
      setSnapshot(null);
      return;
    }
    let mounted = true;
    const loadSnapshot = async () => {
      setSnapshotLoading(true);
      try {
        const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/users/${snapshotUserId}/access-summary`);
        if (!mounted) return;
        setSnapshot(response.data);
      } catch (err) {
        if (!mounted) return;
        setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load user access summary.' });
      } finally {
        if (mounted) setSnapshotLoading(false);
      }
    };
    loadSnapshot();
    return () => {
      mounted = false;
    };
  }, [orgId, snapshotUserId]);

  useEffect(() => {
    if (!showTools) return;
    loadOverrides();
  }, [showTools]);

  const formatMfaPreference = (value) => {
    if (!value) return 'Auto';
    if (value === 'totp') return 'Authenticator App';
    if (value === 'sms') return 'SMS';
    if (value === 'email') return 'Email';
    return value;
  };

  const formatRoleLabel = (role) => {
    if (role === 'role-acct-mgmt') return 'Account Management';
    if (role === 'role-key-mgmt') return 'Key Management';
    if (role === 'role-support') return 'Support';
    return role || 'Unknown';
  };

  const formatMfaPolicy = (value) => {
    if (!value || value === 'any') return 'Any';
    if (value === 'sms_preferred') return 'SMS Preferred';
    if (value === 'sms_only') return 'SMS Only';
    if (value === 'email_only') return 'Email Only';
    if (value === 'totp_preferred') return 'Authenticator Preferred';
    if (value === 'totp_only') return 'Authenticator Only';
    return value;
  };

  const filteredOverrideUsers = useMemo(() => {
    const term = overrideUserFilter.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, overrideUserFilter]);

  const formatOverrideType = (value) => {
    if (value === 'enable_password') return 'Enable Email & Password';
    if (value === 'enable_otp_email') return 'Enable Email One-Time Code';
    if (value === 'mfa_oauth_off') return 'Disable MFA for OAuth';
    if (value === 'mfa_channel_email_only') return 'Set MFA Channel to Email Only';
    return value;
  };

  const formatOverrideScope = (value) => (value === 'user' ? 'User' : 'Organization');

  const formatOverrideExpiry = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const handleApplyOverride = async () => {
    setOverrideSubmitting(true);
    setOverrideStatus({ type: '', message: '' });
    try {
      const payload = {
        scope: overrideScope,
        override_type: overrideType,
        mode: overrideMode,
      };
      if (overrideScope === 'user') {
        payload.user_id = overrideUserId;
      }
      if (overrideMode === 'temporary') {
        payload.duration_minutes = overrideDuration;
      }
      const response = await api.post(`/acct-mgmt/v1/orgs/${orgId}/auth-overrides`, payload);
      setOverrideStatus({ type: 'success', message: response.data?.message || 'Override applied.' });
      loadOverrides();
    } catch (err) {
      setOverrideStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to apply auth override.' });
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const handleRemoveOverride = async (overrideId) => {
    if (!overrideId) return;
    setOverrideRemoving(true);
    setOverrideStatus({ type: '', message: '' });
    try {
      const response = await api.delete(`/acct-mgmt/v1/orgs/${orgId}/auth-overrides/${overrideId}`);
      setOverrideStatus({ type: 'success', message: response.data?.message || 'Override removed.' });
      loadOverrides();
    } catch (err) {
      setOverrideStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to remove override.' });
    } finally {
      setOverrideRemoving(false);
    }
  };

  const handlePasswordAction = async () => {
    if (!passwordActionUserId) return;
    setPasswordActionSubmitting(true);
    setPasswordActionStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${orgId}/actions/force-password-reset`, {
        user_id: passwordActionUserId,
        action: passwordActionType,
      });
      setPasswordActionStatus({ type: 'success', message: response.data?.message || 'Password enforcement applied.' });
    } catch (err) {
      setPasswordActionStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to apply password enforcement.' });
    } finally {
      setPasswordActionSubmitting(false);
    }
  };

  const handleSessionRevoke = async () => {
    if (!sessionUserId) return;
    setSessionSubmitting(true);
    setSessionStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${orgId}/actions/revoke-sessions`, {
        user_id: sessionUserId,
        portal: sessionPortal,
      });
      setSessionStatus({ type: 'success', message: response.data?.message || 'Sessions revoked.' });
    } catch (err) {
      setSessionStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to revoke sessions.' });
    } finally {
      setSessionSubmitting(false);
    }
  };

  const handleRevertOverride = async () => {
    setOverrideReverting(true);
    setOverrideStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${orgId}/auth-overrides/revert-last`);
      setOverrideStatus({ type: 'success', message: response.data?.message || 'Authentication settings reverted.' });
      loadOverrides();
    } catch (err) {
      setOverrideStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to revert authentication settings.' });
    } finally {
      setOverrideReverting(false);
      setShowRevertConfirm(false);
    }
  };

  const formatMethodName = (method) => {
    if (method.provider_code === 'password') return 'Email & Password';
    if (method.provider_code === 'otp' || method.provider_code === 'otp_email') return 'Email One-Time Code';
    if (method.provider_code === 'google') return 'Google';
    if (method.provider_code === 'microsoft') return 'Microsoft';
    if (method.provider_code === 'oidc') return `${method.provider_name || 'Custom OIDC'} (Custom OIDC)`;
    return method.provider_name || method.provider_code;
  };

  const handleClearThrottles = async () => {
    if (!selectedEmail) return;
    setClearLoading(true);
    setThrottleStatus({ type: '', message: '' });
    try {
      await api.post(`/acct-mgmt/v1/orgs/${orgId}/actions/clear-auth-throttles`, {
        email: selectedEmail,
        purposes: selectedPurposes.length > 0 ? selectedPurposes : undefined,
      });
      setThrottleStatus({ type: 'success', message: 'Auth throttles cleared.' });
      setSelectedPurposes([]);
      await loadThrottles(selectedEmail);
    } catch (err) {
      setThrottleStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to clear auth throttles.' });
    } finally {
      setClearLoading(false);
    }
  };

  const confirmReset = async () => {
    if (!selectedUserId) return;
    setResetLoading(true);
    setResetStatus({ type: '', message: '' });
    try {
      await api.post(`/acct-mgmt/v1/orgs/${orgId}/actions/reset-totp`, { user_id: selectedUserId });
      setResetStatus({ type: 'success', message: 'Authenticator reset.' });
      setShowResetConfirm(false);
    } catch (err) {
      setResetStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to reset authenticator.' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setAckLoading(true);
    setStatus({ type: '', message: '' });
    try {
      await api.post(`/acct-mgmt/v1/orgs/${orgId}/actions/acknowledge-verification`, { user_id: snapshotUserId || null });
      setStatus({ type: 'success', message: 'Identity verification acknowledgement recorded.' });
      setShowAcknowledge(false);
      setShowTools(true);
      if (orgId) {
        window.localStorage.setItem(`finbase.ops.showTools.${orgId}`, 'true');
      }
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to record acknowledgement.' });
    } finally {
      setAckLoading(false);
    }
  };

  const handleReopenOrganization = async () => {
    setReopenSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/orgs/${orgId}/reopen`);
      setStatus({
        type: 'success',
        message: `Organization reopened. Restored ${response.data?.restored_users?.length || 0} user membership${(response.data?.restored_users?.length || 0) === 1 ? '' : 's'}.`,
      });
      setShowTools(false);
      window.localStorage.removeItem(`finbase.ops.showTools.${orgId}`);
      window.location.reload();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to reopen organization.' });
    } finally {
      setReopenSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-3">
        <h2 className="h4 fw-bold mb-1">Support Actions</h2>
        <p className="text-muted mb-2">
          {isClosedOrg
            ? 'Verify a former account manager before reopening this organization. All support actions are logged and audited.'
            : 'Verify the user’s identity before performing actions. All support actions are logged and audited.'}
        </p>
      </div>
      {status.message && (
        <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Identity Verification</h5>
          <OpsIdentityVerification
            summary={summary}
            usersOverride={isClosedOrg ? users : null}
            loadingOverride={isClosedOrg ? loadingUsers : false}
            selectionLabel={isClosedOrg ? 'Former Account Manager' : 'Select User'}
          />
          {isClosedOrg && !loadingUsers && (
            <div className="mt-3 pt-3 border-top">
              {(reopenPreview?.eligible_account_managers || []).length === 0 ? (
                <div className="text-muted">
                  No former account managers are eligible for reassociation. This organization cannot currently be reopened through support.
                </div>
              ) : (
                <div className="text-muted small">
                  Only former account managers who were present at closure, remain orphaned, and did not join another organization after closure can be used for verification and reassociation.
                </div>
              )}
            </div>
          )}
          {!showTools && (
            <div className="d-flex justify-content-center align-items-center mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-warning"
                onClick={() => setShowAcknowledge(true)}
                disabled={isClosedOrg && (reopenPreview?.eligible_account_managers || []).length === 0}
              >
                Show Action Tools
              </button>
            </div>
          )}
        </div>
      </div>

      {showTools && isClosedOrg && (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">Reopen Organization</h5>
            <div className="text-muted small mb-3">
              Reopening will reactivate the organization and reassociate only users who were present at closure, are still orphaned, and did not join another organization after this organization was closed.
            </div>
            <div className="mb-3">
              <div className="fw-semibold mb-2">Users to Reassociate</div>
              {(reopenPreview?.restorable_users || []).length === 0 ? (
                <div className="text-muted">No orphaned users are eligible to be reassociated.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reopenPreview?.restorable_users || []).map((user) => (
                        <tr key={user.user_id}>
                          <td>{user.full_name ? `${user.full_name} (${user.email})` : user.email}</td>
                          <td>{(user.role_labels || []).join(', ') || 'No roles'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="mb-3">
              <div className="fw-semibold mb-2">Users Not Reassociated</div>
              {(reopenPreview?.skipped_users || []).length === 0 ? (
                <div className="text-muted">None.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reopenPreview?.skipped_users || []).map((user) => (
                        <tr key={user.user_id}>
                          <td>{user.full_name ? `${user.full_name} (${user.email})` : user.email}</td>
                          <td>{user.skip_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-warning"
              onClick={() => setShowReopenConfirm(true)}
              disabled={reopenSubmitting}
            >
              {reopenSubmitting ? 'Reopening...' : 'Reopen Organization'}
            </button>
          </div>
        </div>
      )}

      {showTools && !isClosedOrg && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">User Access Snapshot</h5>
              <div className="text-muted small mb-3">
                Review a user’s authentication profile and the organization’s current access policies.
              </div>
              <label className="form-label">User</label>
              <input
                className="form-control mb-2"
                value={snapshotFilter}
                onChange={(e) => setSnapshotFilter(e.target.value)}
                placeholder="Type to filter users"
                disabled={loadingUsers}
              />
              <select
                className="form-select"
                value={snapshotUserId}
                onChange={(e) => setSnapshotUserId(e.target.value)}
                disabled={loadingUsers}
              >
                <option value="">Select a user</option>
                {filteredSnapshotUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                  </option>
                ))}
              </select>

              {snapshotLoading && <div className="text-muted mt-3">Loading user access summary...</div>}
              {!snapshotLoading && snapshot && (
                <div className="row g-3 mt-3">
                  <div className="col-lg-6">
                    <div className="fw-semibold mb-2">Organization Policies</div>
                    <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
                      <span className="fw-semibold">Allowed Sign-In Methods:</span>
                      {(() => {
                        const enabledMethods = (snapshot.methods || []).filter((method) => method.enabled);
                        const enabledOidc = (snapshot.oidc_providers || []).filter((provider) => provider.enabled && provider.is_complete);
                        const incompleteOidc = (snapshot.oidc_providers || []).filter((provider) => !provider.is_complete);
                        if (enabledMethods.length === 0 && enabledOidc.length === 0 && incompleteOidc.length === 0) {
                          return <span className="text-muted">None</span>;
                        }
                        return (
                          <div className="d-flex flex-wrap gap-1">
                            {enabledMethods.map((method) => (
                              <span key={`${method.provider_code}-${method.provider_id || method.provider_name}`} className="badge bg-success">
                                {formatMethodName(method)}
                              </span>
                            ))}
                            {enabledOidc.map((provider) => (
                              <span key={provider.public_id || provider.id} className="badge bg-info text-dark">
                                {`OIDC: ${provider.name || 'Custom'}`}
                              </span>
                            ))}
                            {incompleteOidc.map((provider) => (
                              <span key={`incomplete-${provider.public_id || provider.id}`} className="badge bg-warning text-dark">
                                {`OIDC: ${provider.name || 'Custom'} (Incomplete)`}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div><strong>MFA Required:</strong> {snapshot.org?.mfa_required ? 'Yes' : 'No'}</div>
                    <div><strong>MFA Channel Policy:</strong> {formatMfaPolicy(snapshot.org?.mfa_channel_policy)}</div>
                    <div><strong>MFA for OAuth:</strong> {snapshot.org?.mfa_require_oauth ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="col-lg-6">
                    <div className="fw-semibold mb-2">User Auth Profile</div>
                    <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
                      <span className="fw-semibold">Roles:</span>
                      {(snapshot.user?.roles || []).length === 0 ? (
                        <span className="text-muted">None</span>
                      ) : (
                        <div className="d-flex flex-wrap gap-1">
                          {snapshot.user.roles.map((role) => (
                            <span key={role} className="badge bg-secondary">
                              {formatRoleLabel(role)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div><strong>Password Set:</strong> {snapshot.user?.password_set ? 'Yes' : 'No'}</div>
                    <div><strong>MFA Preference:</strong> {formatMfaPreference(snapshot.user?.mfa_preference)}</div>
                    <div><strong>Authenticator Enabled:</strong> {snapshot.user?.totp_enabled ? 'Yes' : 'No'}</div>
                    <div><strong>Phone Verified:</strong> {snapshot.user?.phone_verified_at ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="accordion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="support-actions-overrides">
                <button
                  className={`accordion-button ${openActionSection === 'overrides' ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={openActionSection === 'overrides'}
                  aria-controls="support-actions-overrides-body"
                  onClick={() => setOpenActionSection((prev) => (prev === 'overrides' ? '' : 'overrides'))}
                >
                  Temporary Auth Method Overrides
                </button>
              </h2>
              <div
                id="support-actions-overrides-body"
                className={`accordion-collapse collapse ${openActionSection === 'overrides' ? 'show' : ''}`}
                aria-labelledby="support-actions-overrides"
              >
                <div className="accordion-body">
                  <div className="text-muted small mb-3">
                    Temporarily allow a sign-in method or adjust MFA settings to help users regain access.
                  </div>
                  <div className="row g-3">
                    <div className="col-lg-4">
                      <label className="form-label">Scope</label>
                      <select
                        className="form-select"
                        value={overrideScope}
                        onChange={(e) => {
                          const nextScope = e.target.value;
                          setOverrideScope(nextScope);
                          if (nextScope === 'org') {
                            setOverrideUserId('');
                            setOverrideMode(overrideMode === 'permanent' ? 'permanent' : 'temporary');
                          } else {
                            setOverrideMode('temporary');
                          }
                        }}
                      >
                        <option value="org">Organization</option>
                        <option value="user">Specific User</option>
                      </select>
                    </div>
                    <div className="col-lg-8">
                      <label className="form-label">User</label>
                      <input
                        className="form-control mb-2"
                        value={overrideUserFilter}
                        onChange={(e) => setOverrideUserFilter(e.target.value)}
                        placeholder={overrideScope === 'user' ? 'Type to filter users' : 'User selection is only required for user-scoped overrides'}
                        disabled={loadingUsers || overrideScope !== 'user'}
                      />
                      <select
                        className="form-select"
                        value={overrideUserId}
                        onChange={(e) => setOverrideUserId(e.target.value)}
                        disabled={loadingUsers || overrideScope !== 'user'}
                      >
                        <option value="">Select a user</option>
                        {filteredOverrideUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-6">
                      <label className="form-label">Override Action</label>
                      <select
                        className="form-select"
                        value={overrideType}
                        onChange={(e) => setOverrideType(e.target.value)}
                      >
                        <option value="enable_otp_email">Enable Email One-Time Code</option>
                        <option value="enable_password">Enable Email &amp; Password</option>
                        <option value="mfa_oauth_off" disabled={overrideScope === 'user'}>Disable MFA for OAuth (Org Only)</option>
                        <option value="mfa_channel_email_only">Set MFA Channel to Email Only</option>
                      </select>
                    </div>
                    <div className="col-lg-3">
                      <label className="form-label">Mode</label>
                      <select
                        className="form-select"
                        value={overrideMode}
                        onChange={(e) => setOverrideMode(e.target.value)}
                        disabled={overrideScope === 'user'}
                      >
                        <option value="temporary">Temporary</option>
                        <option value="permanent">Permanent</option>
                      </select>
                      {overrideScope === 'user' && (
                        <div className="form-text">User overrides are always temporary.</div>
                      )}
                    </div>
                    <div className="col-lg-3">
                      <label className="form-label">Duration (Minutes)</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        max="240"
                        value={overrideDuration}
                        onChange={(e) => setOverrideDuration(e.target.value)}
                        disabled={overrideMode !== 'temporary'}
                      />
                    </div>
                  </div>
                  <div className="mt-3 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={() => {
                        if (overrideMode === 'permanent') {
                          setShowPermanentConfirm(true);
                          return;
                        }
                        handleApplyOverride();
                      }}
                      disabled={overrideSubmitting || (overrideScope === 'user' && !overrideUserId)}
                    >
                      {overrideSubmitting
                        ? 'Applying...'
                        : overrideMode === 'permanent'
                          ? 'Update Permanent Auth Setting'
                          : 'Apply Override'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => setShowRevertConfirm(true)}
                      disabled={overrideReverting}
                    >
                      Revert Last Support Change
                    </button>
                  </div>
                  {overrideStatus.message && (
                    <div className={`alert alert-${overrideStatus.type} mt-3`} role="alert">
                      {overrideStatus.message}
                    </div>
                  )}
                  <div className="mt-4">
                    <div className="fw-semibold mb-2">Active Overrides</div>
                    {overridesLoading ? (
                      <div className="text-muted small">Loading overrides...</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Scope</th>
                              <th>Action</th>
                              <th>User</th>
                              <th>Expires</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {overrides.map((override) => (
                              <tr key={override.id}>
                                <td>{formatOverrideScope(override.user_id ? 'user' : 'org')}</td>
                                <td>{formatOverrideType(override.override_type)}</td>
                                <td>{override.user_name ? `${override.user_name} (${override.user_email})` : '—'}</td>
                                <td>{formatOverrideExpiry(override.expires_at)}</td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveOverride(override.id)}
                                    disabled={overrideRemoving}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {overrides.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-muted text-center">No active overrides.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="support-actions-throttles">
                <button
                  className={`accordion-button ${openActionSection === 'throttles' ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={openActionSection === 'throttles'}
                  aria-controls="support-actions-throttles-body"
                  onClick={() => setOpenActionSection((prev) => (prev === 'throttles' ? '' : 'throttles'))}
                >
                  Clear Auth Throttles
                </button>
              </h2>
              <div
                id="support-actions-throttles-body"
                className={`accordion-collapse collapse ${openActionSection === 'throttles' ? 'show' : ''}`}
                aria-labelledby="support-actions-throttles"
              >
                <div className="accordion-body">
                  <div className="text-muted small mb-3">
                    Removes login/MFA throttles for a user so they can retry immediately.
                  </div>
                  <label className="form-label">User</label>
                  <input
                    className="form-control mb-2"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    placeholder="Type to filter users"
                    disabled={loadingUsers}
                  />
                  <select
                    className="form-select"
                    value={selectedEmail}
                    onChange={(e) => setSelectedEmail(e.target.value)}
                    disabled={loadingUsers}
                  >
                    {filteredByEmail.length === 0 && <option value="">No users found</option>}
                    {filteredByEmail.map((user) => (
                      <option key={user.email} value={user.email}>
                        {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-primary mt-3"
                    onClick={handleClearThrottles}
                    disabled={!selectedEmail || clearLoading}
                  >
                    {clearLoading ? 'Clearing…' : (selectedPurposes.length > 0 ? 'Clear Selected' : 'Clear All')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary mt-3 ms-2"
                    onClick={() => loadThrottles(selectedEmail)}
                    disabled={!selectedEmail || throttleLoading}
                  >
                    Refresh List
                  </button>
                  {throttleLoading && <div className="text-muted small mt-2">Loading throttles...</div>}
                  {!throttleLoading && throttles.length > 0 && (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Purpose</th>
                            <th>Key Type</th>
                            <th>Attempts</th>
                            <th>Locked Until</th>
                          </tr>
                        </thead>
                        <tbody>
                          {throttles.map((row) => (
                            <tr key={`${row.purpose}-${row.key_type}-${row.key_value}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedPurposes.includes(row.purpose)}
                                  onChange={() => togglePurpose(row.purpose)}
                                />
                              </td>
                              <td>{formatPurpose(row.purpose)}</td>
                              <td>{row.key_type}</td>
                              <td>{row.attempts}</td>
                              <td>{formatDateTime(row.locked_until)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!throttleLoading && selectedEmail && throttles.length === 0 && (
                    <div className="text-muted small mt-3">No active throttles for this user.</div>
                  )}
                  {throttleStatus.message && (
                    <div className={`alert alert-${throttleStatus.type} mt-3`} role="alert">
                      {throttleStatus.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="support-actions-totp">
                <button
                  className={`accordion-button ${openActionSection === 'totp' ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={openActionSection === 'totp'}
                  aria-controls="support-actions-totp-body"
                  onClick={() => setOpenActionSection((prev) => (prev === 'totp' ? '' : 'totp'))}
                >
                  Reset Authenticator App
                </button>
              </h2>
              <div
                id="support-actions-totp-body"
                className={`accordion-collapse collapse ${openActionSection === 'totp' ? 'show' : ''}`}
                aria-labelledby="support-actions-totp"
              >
                <div className="accordion-body">
                  <div className="text-muted small mb-3">
                    Removes the current authenticator enrollment so the user can set it up again at next login.
                  </div>
                  <label className="form-label">User</label>
                  <input
                    className="form-control mb-2"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    placeholder="Type to filter users"
                    disabled={loadingUsers}
                  />
                  <select
                    className="form-select"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Select a user</option>
                    {filteredUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name ? `${user.full_name} (${user.email})` : user.email}{user.totp_enabled ? ' • Authenticator Enabled' : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-danger mt-3"
                    onClick={() => setShowResetConfirm(true)}
                    disabled={!selectedUserId}
                  >
                    Reset Authenticator
                  </button>
                  {resetStatus.message && (
                    <div className={`alert alert-${resetStatus.type} mt-3`} role="alert">
                      {resetStatus.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="support-actions-password">
                <button
                  className={`accordion-button ${openActionSection === 'password' ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={openActionSection === 'password'}
                  aria-controls="support-actions-password-body"
                  onClick={() => setOpenActionSection((prev) => (prev === 'password' ? '' : 'password'))}
                >
                  Password Reset Enforcement
                </button>
              </h2>
              <div
                id="support-actions-password-body"
                className={`accordion-collapse collapse ${openActionSection === 'password' ? 'show' : ''}`}
                aria-labelledby="support-actions-password"
              >
                <div className="accordion-body">
                  <div className="text-muted small mb-3">
                    Require a user to set or update their password on their next sign-in.
                  </div>
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label className="form-label">User</label>
                      <input
                        className="form-control mb-2"
                        value={passwordActionFilter}
                        onChange={(e) => setPasswordActionFilter(e.target.value)}
                        placeholder="Type to filter users"
                        disabled={loadingUsers}
                      />
                      <select
                        className="form-select"
                        value={passwordActionUserId}
                        onChange={(e) => setPasswordActionUserId(e.target.value)}
                        disabled={loadingUsers}
                      >
                        <option value="">Select a user</option>
                        {filteredPasswordUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-6">
                      <label className="form-label">Action</label>
                      <select
                        className="form-select"
                        value={passwordActionType}
                        onChange={(e) => setPasswordActionType(e.target.value)}
                      >
                        <option value="force_password_reset">Require password update on next password sign-in</option>
                        <option value="force_password_enroll">Require password setup after any sign-in</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-warning mt-3"
                    onClick={handlePasswordAction}
                    disabled={!passwordActionUserId || passwordActionSubmitting}
                  >
                    {passwordActionSubmitting ? 'Applying...' : 'Apply Password Requirement'}
                  </button>
                  {passwordActionStatus.message && (
                    <div className={`alert alert-${passwordActionStatus.type} mt-3`} role="alert">
                      {passwordActionStatus.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h2 className="accordion-header" id="support-actions-sessions">
                <button
                  className={`accordion-button ${openActionSection === 'sessions' ? '' : 'collapsed'}`}
                  type="button"
                  aria-expanded={openActionSection === 'sessions'}
                  aria-controls="support-actions-sessions-body"
                  onClick={() => setOpenActionSection((prev) => (prev === 'sessions' ? '' : 'sessions'))}
                >
                  Revoke Active Sessions
                </button>
              </h2>
              <div
                id="support-actions-sessions-body"
                className={`accordion-collapse collapse ${openActionSection === 'sessions' ? 'show' : ''}`}
                aria-labelledby="support-actions-sessions"
              >
                <div className="accordion-body">
                  <div className="text-muted small mb-3">
                    Invalidates refresh tokens so the user is signed out on their next request.
                  </div>
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label className="form-label">User</label>
                      <input
                        className="form-control mb-2"
                        value={sessionUserFilter}
                        onChange={(e) => setSessionUserFilter(e.target.value)}
                        placeholder="Type to filter users"
                        disabled={loadingUsers}
                      />
                      <select
                        className="form-select"
                        value={sessionUserId}
                        onChange={(e) => setSessionUserId(e.target.value)}
                        disabled={loadingUsers}
                      >
                        <option value="">Select a user</option>
                        {filteredSessionUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-6">
                      <label className="form-label">Portal</label>
                      <select
                        className="form-select"
                        value={sessionPortal}
                        onChange={(e) => setSessionPortal(e.target.value)}
                      >
                        <option value="all">All Portals</option>
                        <option value="account">Account</option>
                        <option value="ops">Ops</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-warning mt-3"
                    onClick={handleSessionRevoke}
                    disabled={!sessionUserId || sessionSubmitting}
                  >
                    {sessionSubmitting ? 'Revoking...' : 'Revoke Sessions'}
                  </button>
                  {sessionStatus.message && (
                    <div className={`alert alert-${sessionStatus.type} mt-3`} role="alert">
                      {sessionStatus.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showRevertConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Revert last support change?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowRevertConfirm(false)}
                    disabled={overrideReverting}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will restore the previous authentication settings for the organization.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowRevertConfirm(false)}
                    disabled={overrideReverting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleRevertOverride}
                    disabled={overrideReverting}
                  >
                    {overrideReverting ? 'Reverting...' : 'Revert'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showPermanentConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Apply permanent auth change?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowPermanentConfirm(false)}
                    disabled={overrideSubmitting}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will update the organization’s authentication settings until they are changed again.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPermanentConfirm(false)}
                    disabled={overrideSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      setShowPermanentConfirm(false);
                      await handleApplyOverride();
                    }}
                    disabled={overrideSubmitting}
                  >
                    {overrideSubmitting ? 'Applying...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showResetConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reset authenticator?</h5>
                  <button type="button" className="btn-close" onClick={() => setShowResetConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will remove the current authenticator enrollment for{' '}
                    <strong>{selectedUser?.email || 'this user'}</strong>. They will need to re‑enroll on next login.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowResetConfirm(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmReset} disabled={resetLoading}>
                    {resetLoading ? 'Resetting…' : 'Reset Authenticator'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showAcknowledge && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm acknowledgement</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAcknowledge(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Per policy, ensure all users impacted by support actions have their identity verified.
                    Continue to acknowledge this requirement and reveal action tools?
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAcknowledge(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-warning" onClick={handleAcknowledge} disabled={ackLoading}>
                    {ackLoading ? 'Recording…' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showReopenConfirm && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reopen Organization?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowReopenConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  Reopening this organization will restore eligible former users, reactivate account access, and notify reassociated users by email.
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowReopenConfirm(false)} disabled={reopenSubmitting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={async () => {
                      setShowReopenConfirm(false);
                      await handleReopenOrganization();
                    }}
                    disabled={reopenSubmitting}
                  >
                    {reopenSubmitting ? 'Reopening...' : 'Confirm Reopen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default OpsOrgActions;
